import {
  Center,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spacer,
  Tooltip,
} from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import {
  AddIcon,
  ButtonWrapper,
  CreateNewFolderIcon,
  HistoryIcon,
  ImportSchematisationIcon,
  RanaButton,
  RanaContextMenu,
  RanaTable,
  UploadFilesIcon,
  UploadFolderIcon,
} from "@nens/ranalib";
import { displayBytes } from "@nens/shared-utils";

import { FoldersBreadcrumb } from "@/components/folders-breadcrumb";
import { TableEmptyFiles } from "@/components/table-empty-file";
import { useCanSeeModelSchematisation } from "@/hooks/use-can-see-model-schematisation";
import { SchematisationProjectImportModals } from "@/lib/catalogue";
import { Profile } from "@/lib/profile";
import { FileDataTypeEnum, RanaFileIcon, getFileDisplayType } from "@/lib/rana-file-data-type";
import { SchematisationRevisionHistory } from "@/lib/schematisation-revision-history";
import { fileNameColumnString } from "@/translations";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";
import { getPrettyTime, getPrettyTimeFromNow } from "@/utils/get-pretty-time";
import { isTypedDescriptor } from "@/utils/is-descriptor-type";

import { ProjectFileModelSchemaToCatalogue } from "../project-file-model-schema-to-catalogue/project-file-model-schema-to-catalogue";

import { AddFolderModal } from "./components/add-folder-modal";
import { AddFolderWithSelectionModal } from "./components/add-folder-with-selection";
import { ProjectConfirmMoveModal } from "./components/project-file-confirm-move-modal";
import {
  ProjectConfirmRevertVersionModal,
  RevertFileToVersionButton,
} from "./components/project-file-confirm-revert-version-modal";
import { ProjectFileOrDirectoryCopyToModal } from "./components/project-file-copy-to-modal";
import { ProjectFileOrDirectoryDeleteModal } from "./components/project-file-delete-modal";
import { ProjectFileDetails } from "./components/project-file-details";
import { ProjectFileOrDirectoryDuplicateModal } from "./components/project-file-duplicate-modal";
import { ProjectFileHistory } from "./components/project-file-history";
import { ProjectFileOrDirectoryMoveModal } from "./components/project-file-move-modal";
import { ProjectFileNoDesktopClientModal } from "./components/project-file-no-desktop-client-modal";
import { ProjectFileOrDirectoryRenameModal } from "./components/project-file-rename-modal";
import { SchematisationLiveSiteAction } from "./components/schematisation-live-site-action";
import { useProjectFilesList } from "./hooks/use-project-files-list";
import { getSchematisationId } from "./utils/get-schematisation-id";

export function ProjectFilesList() {
  const {
    searchString,
    setSearchString,
    file,
    clearFile,
    onDirectoryOrFileClick,
    paths,
    setPaths,
    fileName,
    fileRef,
    path,
    intl,
    files,
    tableDataIds,
    getRootProps,
    getInputProps,
    isDragActive,
    openDropzone,
    openFolderPicker,
    isSkeleton,
    isLoading,
    orderings,
    setOrdering,
    dontConfirmMove,
    setDontConfirmMove,
    openModal,
    addDirectoryOpen,
    setAddDirectoryOpen,
    remameDirectoryOrFile,
    setRenameDirectoryOrFile,
    setOpenModal,
    closeModal,
    getActions,
    moveFilesByRowIds,
    moveProjectFileOrDirectoryMutation,
    canWriteProject,
    tableActions,
    project,
    canReadDatasetList,
    viewFileHistory,
    setViewFileHistory,
    selectedRevisionId,
    setSelectedRevisionId,
  } = useProjectFilesList();

  const canSeeModelSchematisation = useCanSeeModelSchematisation();

  if (viewFileHistory) {
    const schematisationId = file ? getSchematisationId(file) : undefined;
    if (schematisationId !== undefined && canSeeModelSchematisation) {
      return (
        <SchematisationRevisionHistory
          setSelectedRevision={setSelectedRevisionId}
          schematisationId={schematisationId}
          backLabel={
            fileName ? (
              <FormattedMessage id="button.file" defaultMessage="File" />
            ) : (
              <FormattedMessage id="button.files" defaultMessage="Files" />
            )
          }
          back={() => setViewFileHistory(false)}
        />
      );
    }

    return (
      <ProjectFileHistory
        fileRef={fileRef}
        path={path}
        fileName={fileName}
        back={() => setViewFileHistory(false)}
      />
    );
  }

  const isSchematisation = file?.dataType === FileDataTypeEnum.Schematisation;
  const fileDescriptor = file?.descriptor || undefined;
  const schematisationDescriptor = isTypedDescriptor(fileDescriptor, "threedi_schematisation")
    ? fileDescriptor
    : null;
  const schematisationId = schematisationDescriptor ? schematisationDescriptor.meta?.id : undefined;

  let historyButtonLabel = <FormattedMessage id="button.history" defaultMessage="History" />;
  if (file) {
    if (isSchematisation && canSeeModelSchematisation) {
      historyButtonLabel = (
        <FormattedMessage id="button.revision_history" defaultMessage="Revision history" />
      );
    } else {
      historyButtonLabel = (
        <FormattedMessage id="button.file_history" defaultMessage="File History" />
      );
    }
  }

  return (
    <div>
      {openModal?.type === "revert-to-version" && (
        <ProjectConfirmRevertVersionModal
          fileId={openModal.fileId}
          fileRef={openModal.ref}
          onClose={closeModal}
        />
      )}
      {openModal?.type === "dont-have-rana-desktop-client" && (
        <ProjectFileNoDesktopClientModal onClose={closeModal} />
      )}
      {openModal?.type === "duplicate" && (
        <ProjectFileOrDirectoryDuplicateModal
          existingFileIds={tableDataIds}
          directoryOrFile={openModal.item}
          onClose={closeModal}
        />
      )}
      {openModal?.type === "copy-to" && (
        <ProjectFileOrDirectoryCopyToModal
          items={openModal.items}
          onClose={closeModal}
          initialPaths={paths}
        />
      )}
      {openModal?.type === "confirm-move" && (
        <ProjectConfirmMoveModal
          moveFiles={openModal.moveFiles}
          setDontConfirmMove={setDontConfirmMove}
          dontConfirmMove={dontConfirmMove}
          onClose={closeModal}
        />
      )}
      {openModal?.type === "new-folder-with-selection" && (
        <AddFolderWithSelectionModal
          basePath={path}
          existingFileIds={tableDataIds}
          items={openModal.items}
          onClose={closeModal}
          moveProjectFileOrDirectoryMutation={moveProjectFileOrDirectoryMutation}
        />
      )}
      {openModal?.type === "import" && (
        <SchematisationProjectImportModals
          from="project"
          isOpen
          project={project}
          path={path}
          onClose={closeModal}
        />
      )}
      {openModal?.type === "submit-schematisation" && (
        <ProjectFileModelSchemaToCatalogue fileId={openModal.item.id} onClose={closeModal} />
      )}
      {openModal?.type === "delete" && (
        <ProjectFileOrDirectoryDeleteModal
          items={openModal.items}
          onClose={() => {
            closeModal();
            clearFile();
          }}
        />
      )}
      {openModal?.type === "move" ? (
        <ProjectFileOrDirectoryMoveModal
          initialPaths={paths}
          items={openModal.items}
          onClose={closeModal}
          moveProjectFileOrDirectoryMutation={moveProjectFileOrDirectoryMutation}
        />
      ) : null}
      {remameDirectoryOrFile ? (
        <ProjectFileOrDirectoryRenameModal
          existingFileIds={tableDataIds}
          directoryOrFile={remameDirectoryOrFile}
          onClose={() => setRenameDirectoryOrFile(null)}
        />
      ) : null}
      {addDirectoryOpen ? (
        <AddFolderModal
          basePath={path}
          onClose={() => setAddDirectoryOpen(false)}
          existingFileIds={tableDataIds}
        />
      ) : null}
      <Flex direction="column" gap={4}>
        <Flex flexWrap={{ base: "wrap", md: "nowrap" }} gap={1} alignItems="center">
          <FoldersBreadcrumb
            paths={paths}
            fileName={fileName}
            setPaths={(paths) => {
              setPaths(paths);
              clearFile();
            }}
            moveFilesByRowIds={moveFilesByRowIds}
          />
          <Spacer />
          <ButtonWrapper flexWrap={{ base: "wrap", md: "nowrap" }}>
            {file && fileRef && (
              <RevertFileToVersionButton
                action={() =>
                  setOpenModal({ type: "revert-to-version", fileId: file.id, ref: fileRef })
                }
              />
            )}
            {schematisationId !== undefined && (
              <SchematisationLiveSiteAction schematisationId={schematisationId} />
            )}
            <RanaButton
              leftIcon={<HistoryIcon />}
              variant="secondary"
              onClick={() => setViewFileHistory(true)}
            >
              {historyButtonLabel}
            </RanaButton>
            {file ? (
              <RanaContextMenu
                color={undefined} // overrides value set by RanaContextMenu
                w="button"
                variant="secondary"
                actions={getActions(file)}
              />
            ) : (
              canWriteProject && (
                <Flex flexWrap="nowrap">
                  <Menu>
                    <MenuButton as={RanaButton} leftIcon={<AddIcon />} variant="primary">
                      <FormattedMessage id="button.add" defaultMessage="Add" />
                    </MenuButton>

                    <MenuList>
                      <Tooltip
                        placement="left"
                        hasArrow
                        label={intl.formatMessage({
                          id: "tooltip.existing_files",
                          defaultMessage: "Existing file(s) from your computer",
                        })}
                      >
                        <MenuItem icon={<UploadFilesIcon />} onClick={openDropzone}>
                          <FormattedMessage
                            id="menuitem.upload_file"
                            defaultMessage="Upload file(s)"
                          />
                        </MenuItem>
                      </Tooltip>

                      <Tooltip
                        placement="left"
                        hasArrow
                        label={intl.formatMessage({
                          id: "tooltip.existing_folders",
                          defaultMessage: "Existing folder from your computer",
                        })}
                      >
                        <MenuItem icon={<UploadFolderIcon />} onClick={openFolderPicker}>
                          <FormattedMessage
                            id="menuitem.upload_folder"
                            defaultMessage="Upload folder"
                          />
                        </MenuItem>
                      </Tooltip>
                      <MenuDivider />
                      <Tooltip
                        placement="left"
                        hasArrow
                        label={intl.formatMessage({
                          id: "tooltip.new_empty_folder",
                          defaultMessage: "Create new empty folder",
                        })}
                      >
                        <MenuItem
                          icon={<CreateNewFolderIcon />}
                          onClick={() => setAddDirectoryOpen(true)}
                        >
                          <FormattedMessage
                            id="menuitem.new_folder"
                            defaultMessage="Create new folder"
                          />
                        </MenuItem>
                      </Tooltip>
                      {canReadDatasetList && (
                        <>
                          <MenuDivider />
                          <Tooltip
                            placement="left"
                            hasArrow
                            label={intl.formatMessage({
                              id: "tooltip.import_schematisation_file",
                              defaultMessage: "Add a model schematisation from the catalogue",
                            })}
                          >
                            <MenuItem
                              icon={<ImportSchematisationIcon />}
                              onClick={() => setOpenModal({ type: "import" })}
                            >
                              <FormattedMessage
                                id="menuitem.schema"
                                defaultMessage="Add a Model Schematisation"
                              />
                            </MenuItem>
                          </Tooltip>
                        </>
                      )}
                    </MenuList>
                  </Menu>
                </Flex>
              )
            )}
          </ButtonWrapper>
        </Flex>
        {file ? (
          <ProjectFileDetails
            fileRef={fileRef}
            file={file}
            selectedRevisionId={selectedRevisionId}
          />
        ) : (
          <div {...getRootProps({ onClick: (e) => e.stopPropagation() })}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <Center
                h="2xl"
                borderStyle="dashed"
                borderColor="brand.analytic"
                borderWidth="thick"
                bg="brand.cloudy"
                rounded="base"
                w="full"
                color="brand.analytic"
              >
                <FormattedMessage
                  id="dropzone.drop_files_here"
                  defaultMessage="Drop your files here to upload"
                />
              </Center>
            ) : (
              <RanaTable
                ordering={orderings}
                setOrdering={setOrdering}
                hasRowActions
                columnDefinitions={[
                  {
                    width: "table_cell",
                    title: fileNameColumnString(intl),
                    orderingField: "id",
                  },
                  {
                    width: "table_cell_xs",
                    title: intl.formatMessage({ id: "column.type", defaultMessage: "Type" }),
                  },
                  {
                    width: "table_cell_xxs",
                    title: intl.formatMessage({ id: "column.size", defaultMessage: "Size" }),
                    orderingField: "size",
                  },
                  {
                    width: "table_cell_xs",
                    title: intl.formatMessage({
                      id: "column.added_by",
                      defaultMessage: "Added by",
                    }),
                    orderingField: "user",
                  },
                  {
                    width: "table_cell_xs",
                    title: intl.formatMessage({
                      id: "column.last_modified",
                      defaultMessage: "Last modified",
                    }),
                    orderingField: "lastModified",
                  },
                ]}
                tableDataIds={tableDataIds}
                isSkeleton={isSkeleton}
                isLoading={isLoading}
                tableActions={canWriteProject ? tableActions : []}
                emptyElement={<TableEmptyFiles open={openDropzone} />}
              >
                <RanaTable.Filters
                  search={{
                    placeholder: intl.formatMessage({
                      id: "placeholder.find_files",
                      defaultMessage: "Find files",
                    }),
                    searchString,
                    setSearchString,
                  }}
                />
                <RanaTable.Container>
                  <RanaTable.Header />
                  <RanaTable.Body>
                    {files.map((fileOrDirectory) => {
                      const name = getNameFromFileId(fileOrDirectory.id);
                      const isFile = fileOrDirectory.type === "file";
                      return (
                        <RanaTable.Row
                          key={fileOrDirectory.id}
                          rowId={fileOrDirectory.id}
                          rowActions={getActions(fileOrDirectory)}
                          onClick={() => onDirectoryOrFileClick(fileOrDirectory)}
                          isDraggable
                          onDropInto={
                            isFile
                              ? undefined
                              : (rowIds) => {
                                  moveFilesByRowIds(rowIds, fileOrDirectory.id);
                                }
                          }
                        >
                          <RanaTable.Cell title={name}>
                            <RanaFileIcon fileOrDirectory={fileOrDirectory} />
                            {name}
                          </RanaTable.Cell>
                          <RanaTable.Cell>{getFileDisplayType(fileOrDirectory)}</RanaTable.Cell>
                          <RanaTable.Cell>
                            {isFile && fileOrDirectory.dataType !== FileDataTypeEnum.Schematisation
                              ? displayBytes(fileOrDirectory.size)
                              : ""}
                          </RanaTable.Cell>
                          <RanaTable.Cell>
                            {isFile ? <Profile inTable user={fileOrDirectory.user} /> : ""}
                          </RanaTable.Cell>
                          <RanaTable.Cell
                            title={isFile ? getPrettyTime(fileOrDirectory.lastModified) : ""}
                          >
                            {isFile ? getPrettyTimeFromNow(fileOrDirectory.lastModified) : ""}
                          </RanaTable.Cell>
                        </RanaTable.Row>
                      );
                    })}
                  </RanaTable.Body>
                </RanaTable.Container>
              </RanaTable>
            )}
          </div>
        )}
      </Flex>
    </div>
  );
}
